/**
 * Editor di un capitolato: testata, voci, pesi, formule, versioning e lock.
 *
 * **La UI rispecchia le tre invarianti della route, non le aggira.** Sono nella route perché
 * Dataverse non le sa esprimere, ma un form che lascia editare un capitolato bloccato per poi
 * prendere un 409 in faccia sarebbe peggio di nessun form: qui i comandi che il server rifiuterebbe
 * non ci sono, e il perché è scritto in pagina.
 *
 *   1. un solo capitolato `Attivo` per serie di versioni — attivarne uno archivia la versione
 *      precedente, e la risposta di `/attiva` dice quali. Capitolati diversi che coprono le stesse
 *      tipologie possono essere attivi insieme (ADR-0006);
 *   2. una nuova versione è un clone con versione incrementata, non una modifica;
 *   3. un capitolato bloccato o archiviato è immutabile — con l'eccezione voluta
 *      `Attivo → Archiviato`, che la esegue l'attivazione della versione successiva.
 *
 * **I pesi non devono sommare a 100.** Si normalizzano in comparazione (sprint 05): il totale è
 * mostrato perché serve a chi compila per vedere gli equilibri, non perché ci sia un vincolo da
 * rispettare. Nessun avviso «manca 12 a 100», che indurrebbe a inventare pesi.
 *
 * **Master (ADR-0007).** Un master compone dei capitolati semplici (i sub), ciascuno a una versione
 * precisa e con un peso, più le sue voci proprie: il «Pacchetto». Le tipologie le eredita dai sub;
 * la composizione si cambia solo in Bozza; si attiva solo con tutti i sub attivi. Un sub superato
 * da una versione nuova resta puntato finché non si usa «aggiorna ai sub correnti».
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import { capitolatiApi, anagraficheApi } from "../services/acquistiApi";
import FormVoceGuidato from "../components/FormVoceGuidato";
import {
  Campo,
  Testo,
  AreaTesto,
  Numero,
  Scelta,
  Avviso,
  Caricamento,
  SceltaMultipla,
} from "../components/campi";
import {
  STATO_CAPITOLATO,
  STATO_CAPITOLATO_BADGE,
  CAPITOLATO_BOZZA,
  CAPITOLATO_ARCHIVIATO,
  CAPITOLATO_SEMPLICE,
  SEZIONE_VOCE,
  TIPO_RISPOSTA,
  FORMULA,
  PUNTEGGIO_MASSIMO_DEFAULT,
  opzioni,
} from "../constants/optionSet";
import { dataIt, etichetta } from "../utils/formato";

const VOCE_VUOTA = {
  nome: "",
  sezione: "",
  ordine: "",
  descrizione: "",
  obbligatorio: false,
  peso: "",
  punteggioMassimo: PUNTEGGIO_MASSIMO_DEFAULT,
  tipoRisposta: "",
  unitaMisura: "",
  formula: "",
  sogliaMinima: "",
  sogliaMassima: "",
  note: "",
  criterioValutazioneId: "",
  servizioId: "",
};

/**
 * I sub di un master. In Bozza si aggiungono, si pesano, si riordinano e si tolgono; fuori dalla
 * Bozza è sola lettura, come il server.
 */
function ComponentiMaster({ cap, modificabile, azione, ricarica }) {
  const [candidati, setCandidati] = useState([]);
  const [nuovo, setNuovo] = useState({ subId: "", peso: "" });
  const [pesi, setPesi] = useState({});
  const componenti = cap.componenti || [];

  useEffect(() => {
    if (!modificabile) return;
    capitolatiApi
      .list({ tipo: CAPITOLATO_SEMPLICE })
      .then((r) => setCandidati(r.value || []))
      .catch(() => {});
  }, [modificabile]);

  useEffect(() => {
    setPesi(Object.fromEntries(componenti.map((k) => [k.id, k.peso ?? ""])));
  }, [cap]); // eslint-disable-line react-hooks/exhaustive-deps

  const presenti = new Set(componenti.map((k) => k.subId?.toLowerCase()));
  const opzioniSub = candidati
    .filter(
      (c) =>
        c.stato !== CAPITOLATO_ARCHIVIATO && !presenti.has(c.id.toLowerCase()),
    )
    .map((c) => ({
      value: c.id,
      label: `${c.nome} v${c.versione} — ${c.statoLabel}`,
    }));

  const aggiungi = async (e) => {
    e.preventDefault();
    if (!nuovo.subId) return;
    const ok = await azione(
      () => capitolatiApi.creaComponente(cap.id, nuovo),
      () => "Sub-capitolato aggiunto",
    );
    if (ok) {
      setNuovo({ subId: "", peso: "" });
      ricarica();
    }
  };

  const salvaPeso = async (k) => {
    const valore = pesi[k.id];
    if (String(valore ?? "") === String(k.peso ?? "")) return;
    const ok = await azione(
      () => capitolatiApi.aggiornaComponente(cap.id, k.id, { peso: valore }),
      () => "Peso aggiornato",
    );
    if (ok) ricarica();
  };

  /** Riordino: si riassegna `ordine` 1..n con i due scambiati; il primo dà la tipologia principale. */
  const sposta = async (i, direzione) => {
    const j = i + direzione;
    if (j < 0 || j >= componenti.length) return;
    const ordinati = [...componenti];
    [ordinati[i], ordinati[j]] = [ordinati[j], ordinati[i]];
    const ok = await azione(async () => {
      for (const [n, k] of ordinati.entries()) {
        if (k.ordine !== n + 1) {
          await capitolatiApi.aggiornaComponente(cap.id, k.id, {
            ordine: n + 1,
          });
        }
      }
      return true;
    });
    if (ok) ricarica();
  };

  const togli = async (k) => {
    if (!window.confirm(`Togliere «${k.sub?.nome}» dal master?`)) return;
    const ok = await azione(
      () => capitolatiApi.eliminaComponente(cap.id, k.id),
      () => "Sub-capitolato tolto",
    );
    if (ok) ricarica();
  };

  const aggiornaCorrenti = async () => {
    const res = await azione(
      () => capitolatiApi.aggiornaComponenti(cap.id),
      (r) => r.message,
    );
    if (res) ricarica();
  };

  const superati = componenti.filter((k) => k.superato);
  const pesoTotale =
    componenti.reduce((s, k) => s + (Number(k.peso) || 0), 0) +
    (Number(cap.pesoPacchetto) || 0);

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-semibold text-gray-900">Sub-capitolati</h2>
        <span className="text-sm text-gray-500">
          {componenti.length} sub · peso totale con il Pacchetto{" "}
          <strong>{pesoTotale}</strong>
        </span>
        {modificabile && componenti.length > 0 && (
          <button
            type="button"
            className="btn-secondary ml-auto"
            onClick={aggiornaCorrenti}
            title="Porta ogni sub sulla versione attiva della sua serie"
          >
            Aggiorna ai sub correnti
          </button>
        )}
      </div>

      {superati.length > 0 && (
        <Avviso tipo="attenzione">
          {superati.length === 1
            ? "Un sub è stato superato"
            : "Alcuni sub sono stati superati"}{" "}
          da una versione più recente:{" "}
          {superati
            .map((k) => `«${k.sub?.nome}» v${k.sub?.versione}`)
            .join(", ")}
          .{" "}
          {modificabile
            ? "Usa «Aggiorna ai sub correnti» prima di attivare."
            : "Per aggiornarli crea una nuova versione del master."}
        </Avviso>
      )}

      {componenti.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          Nessun sub-capitolato. Un master senza sub non si può attivare.
        </p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-md">
          {componenti.map((k, i) => (
            <div
              key={k.id}
              className="p-3 flex flex-wrap gap-x-4 gap-y-2 items-center"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to={`/capitolati/${k.subId}`}
                  className="font-medium text-acquisti-700 hover:underline"
                >
                  {k.sub?.nome || k.nome}
                </Link>{" "}
                <span className="text-gray-400">v{k.sub?.versione ?? "?"}</span>
                <span
                  className={`${STATO_CAPITOLATO_BADGE[k.sub?.stato] || "badge-gray"} ml-2`}
                >
                  {k.sub?.statoLabel || "?"}
                </span>
                {k.superato && (
                  <span className="badge-warning ml-2">superato</span>
                )}
                {i === 0 && (
                  <p className="text-xs text-gray-500">
                    il primo dà la tipologia principale del master
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600">
                peso
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className="input w-20"
                  value={pesi[k.id] ?? ""}
                  disabled={!modificabile}
                  onChange={(e) =>
                    setPesi((p) => ({ ...p, [k.id]: e.target.value }))
                  }
                  onBlur={() => salvaPeso(k)}
                />
              </label>
              {modificabile && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    className="px-2 py-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                    onClick={() => sposta(i, -1)}
                    disabled={i === 0}
                    title="Sposta su"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                    onClick={() => sposta(i, 1)}
                    disabled={i === componenti.length - 1}
                    title="Sposta giù"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="px-2 py-1 text-red-600 hover:underline text-sm"
                    onClick={() => togli(k)}
                  >
                    Togli
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modificabile && (
        <form
          onSubmit={aggiungi}
          className="grid grid-cols-1 md:grid-cols-[1fr_8rem_auto] gap-3 items-end"
        >
          <Campo
            label="Aggiungi un sub-capitolato"
            aiuto="Un capitolato semplice, non archiviato. Per attivare il master servono tutti i sub attivi."
          >
            <Scelta
              valore={nuovo.subId}
              onChange={(v) => setNuovo((n) => ({ ...n, subId: v }))}
              opzioni={opzioniSub}
              vuoto="scegli…"
            />
          </Campo>
          <Campo label="Peso">
            <Numero
              valore={nuovo.peso}
              onChange={(v) => setNuovo((n) => ({ ...n, peso: v }))}
              placeholder="10"
            />
          </Campo>
          <button type="submit" className="btn-primary" disabled={!nuovo.subId}>
            Aggiungi
          </button>
        </form>
      )}
    </div>
  );
}

export default function CapitolatoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canPerformAction, user } = useAuth();
  const puoScrivere = canPerformAction("capitolati.gestisci");
  const isAdmin = user?.ruolo === 100000000;

  const [cap, setCap] = useState(null);
  const [testata, setTestata] = useState({
    nome: "",
    descrizione: "",
    note: "",
    pesoPacchetto: "",
  });
  const [tipologieIds, setTipologieIds] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [criteri, setCriteri] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);
  const [inCorso, setInCorso] = useState(false);
  const [voceInModifica, setVoceInModifica] = useState(null); // id | "nuova" | null

  useEffect(() => {
    Promise.allSettled([
      anagraficheApi.tipologieFornitura(),
      anagraficheApi.criteriValutazione(),
    ]).then(([t, c]) => {
      if (t.status === "fulfilled") setTipologie(t.value.value || []);
      if (c.status === "fulfilled") setCriteri(c.value.value || []);
    });
  }, []);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const c = await capitolatiApi.get(id);
      setCap(c);
      setTestata({
        nome: c.nome ?? "",
        descrizione: c.descrizione ?? "",
        note: c.note ?? "",
        pesoPacchetto: c.pesoPacchetto ?? "",
      });
      setTipologieIds((c.tipologieCoperte || []).map((t) => t.id));
      setErrore(null);
    } catch (e) {
      setErrore(e.data?.message || e.message || "Capitolato non trovato");
    } finally {
      setCaricamento(false);
    }
  }, [id]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (caricamento) return <Caricamento testo="Caricamento capitolato…" />;
  if (!cap) return <Avviso tipo="errore">{errore || "Non trovato"}</Avviso>;

  /** Le stesse due condizioni di `motivoNonModificabile` nella route. */
  const modificabile = !cap.bloccato && cap.stato !== 100000002;
  const inBozza = cap.stato === CAPITOLATO_BOZZA;
  const editabile = puoScrivere && modificabile;

  const azione = async (fn, messaggio) => {
    setInCorso(true);
    setErrore(null);
    try {
      const res = await fn();
      if (messaggio) showToast(messaggio(res), "success");
      return res;
    } catch (e) {
      setErrore(e.data?.message || e.message || "Operazione fallita");
      return null;
    } finally {
      setInCorso(false);
    }
  };

  const salvaTestata = async (e) => {
    e.preventDefault();
    const { pesoPacchetto, ...body } = testata;
    // Il master eredita le tipologie dai sub; il peso del Pacchetto, come le tipologie, solo in Bozza.
    if (cap.master) {
      if (inBozza) body.pesoPacchetto = pesoPacchetto;
    } else if (inBozza) {
      body.tipologieIds = tipologieIds;
    }
    const ok = await azione(
      () => capitolatiApi.update(id, body),
      () => "Capitolato aggiornato",
    );
    if (ok) carica();
  };

  const attiva = async () => {
    const res = await azione(
      () => capitolatiApi.attiva(id),
      (r) =>
        [
          r?.archiviati?.length
            ? `Attivato; archiviate ${r.archiviati.length} versioni precedenti`
            : "Capitolato attivato",
          ...(r?.avvisi || []),
        ].join(". "),
    );
    if (res) carica();
  };

  const nuovaVersione = async () => {
    const res = await azione(
      () => capitolatiApi.nuovaVersione(id),
      (r) =>
        `Creata la versione ${r.versione} con ${r.vociCopiate} voci` +
        (r.componentiCopiati ? ` e ${r.componentiCopiati} sub` : ""),
    );
    if (res?.id) navigate(`/capitolati/${res.id}`);
  };

  const blocca = async () => {
    const motivo = window.prompt(
      "Perché si blocca questo capitolato? Il testo resta come traccia per chi lo leggerà dopo.",
    );
    if (!motivo) return;
    const ok = await azione(
      () => capitolatiApi.blocca(id, motivo),
      () => "Capitolato bloccato",
    );
    if (ok) carica();
  };

  const sblocca = async () => {
    if (
      !window.confirm(
        "Sbloccare il capitolato? È un'uscita di sicurezza: se una gara lo referenzia, " +
          "modificarlo altera lo storico delle comparazioni già fatte.",
      )
    )
      return;
    const ok = await azione(
      () => capitolatiApi.sblocca(id),
      () => "Capitolato sbloccato",
    );
    if (ok) carica();
  };

  const elimina = async () => {
    if (!window.confirm("Eliminare questa bozza e tutte le sue voci?")) return;
    const ok = await azione(
      () => capitolatiApi.remove(id),
      () => "Capitolato eliminato",
    );
    if (ok) navigate("/capitolati");
  };

  const salvaVoce = async (voce) => {
    const nuova = voceInModifica === "nuova";
    const ok = await azione(
      () =>
        nuova
          ? capitolatiApi.creaVoce(id, voce)
          : capitolatiApi.aggiornaVoce(id, voceInModifica, voce),
      () => (nuova ? "Voce aggiunta" : "Voce aggiornata"),
    );
    if (ok) {
      setVoceInModifica(null);
      carica();
    }
  };

  const eliminaVoce = async (voceId, nome) => {
    if (!window.confirm(`Eliminare la voce «${nome}»?`)) return;
    const ok = await azione(
      () => capitolatiApi.eliminaVoce(id, voceId),
      () => "Voce eliminata",
    );
    if (ok) carica();
  };

  /**
   * Riordino: si scambia `ordine` con la voce adiacente **della stessa sezione**, perché
   * l'ordinamento del server è (sezione, ordine, nome) e spostare fra sezioni sarebbe un cambio
   * di sezione, non un riordino.
   */
  const sposta = async (voce, direzione) => {
    const sorelle = cap.voci.filter((v) => v.sezione === voce.sezione);
    const i = sorelle.findIndex((v) => v.id === voce.id);
    const j = i + direzione;
    if (j < 0 || j >= sorelle.length) return;
    const altra = sorelle[j];

    setInCorso(true);
    try {
      await capitolatiApi.aggiornaVoce(id, voce.id, { ordine: j + 1 });
      await capitolatiApi.aggiornaVoce(id, altra.id, { ordine: i + 1 });
      await carica();
    } catch (e) {
      setErrore(e.data?.message || e.message);
    } finally {
      setInCorso(false);
    }
  };

  // Il master chiede le voci dei sub: basta un sub. Il server verifica il resto (sub attivi, pesi).
  const attivabile = cap.master
    ? (cap.componenti || []).length > 0
    : (cap.voci || []).length > 0;
  // Le voci del master sono del Pacchetto: niente servizio, i servizi stanno nei sub.
  const servizi = cap.master ? [] : cap.tipologieCoperte || [];

  const perSezione = {};
  for (const v of cap.voci || []) {
    const k = v.sezione ?? "nessuna";
    (perSezione[k] ||= []).push(v);
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/capitolati"
          className="text-sm text-acquisti-700 hover:underline"
        >
          ← Capitolati
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">
          {cap.nome} <span className="text-gray-400">v{cap.versione}</span>
        </h1>
        <span className={STATO_CAPITOLATO_BADGE[cap.stato] || "badge-gray"}>
          {cap.statoLabel || etichetta(STATO_CAPITOLATO, cap.stato)}
        </span>
        {cap.bloccato && <span className="badge-danger">bloccato</span>}
        {cap.master && <span className="badge-info">master</span>}
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {!cap.master && cap.inMaster?.length > 0 && (
        <Avviso tipo="info">
          È un sub-capitolato di{" "}
          {cap.inMaster.map((m, i) => (
            <React.Fragment key={m.id}>
              {i > 0 && ", "}
              <Link
                className="text-acquisti-700 hover:underline"
                to={`/capitolati/${m.id}`}
              >
                {m.nome} v{m.versione}
              </Link>{" "}
              ({m.statoLabel})
            </React.Fragment>
          ))}
          . Una sua nuova versione non entra da sola nei master: va aggiornata
          dal master.
        </Avviso>
      )}

      {cap.bloccato && (
        <Avviso tipo="attenzione">
          <strong>Capitolato bloccato</strong>
          {cap.dataBloccato ? ` dal ${dataIt(cap.dataBloccato)}` : ""}: è
          immutabile per non alterare lo storico delle comparazioni.
          {cap.motivoBlocco && (
            <div className="mt-1 italic">«{cap.motivoBlocco}»</div>
          )}
          <div className="mt-1">
            Per modificarlo si crea una nuova versione e si lavora su quella.
          </div>
        </Avviso>
      )}

      {!cap.bloccato && cap.stato === 100000002 && (
        <Avviso tipo="info">
          Il capitolato è <strong>archiviato</strong>: è la storia di gare già
          fatte e non si modifica più. Per ripartire da qui, crea una nuova
          versione.
        </Avviso>
      )}

      {!puoScrivere && (
        <Avviso tipo="info">
          Sola lettura: l'azione <code>capitolati.gestisci</code> non è concessa
          al tuo ruolo.
        </Avviso>
      )}

      {/* Comandi: ci sono solo quelli che il server accetterebbe davvero. */}
      {puoScrivere && (
        <div className="flex flex-wrap gap-2">
          {modificabile && inBozza && (
            <button
              type="button"
              className="btn-primary"
              onClick={attiva}
              disabled={inCorso || !attivabile}
              title={
                attivabile
                  ? "Archivia la versione attiva di questo capitolato"
                  : cap.master
                    ? "Serve almeno un sub-capitolato"
                    : "Serve almeno una voce"
              }
            >
              Attiva
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={nuovaVersione}
            disabled={inCorso}
          >
            Nuova versione
          </button>
          {modificabile && cap.stato === 100000001 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={blocca}
              disabled={inCorso}
            >
              Blocca
            </button>
          )}
          {cap.bloccato && isAdmin && (
            <button
              type="button"
              className="btn-danger"
              onClick={sblocca}
              disabled={inCorso}
            >
              Sblocca (uscita di sicurezza)
            </button>
          )}
          {modificabile && inBozza && (
            <button
              type="button"
              className="btn-danger ml-auto"
              onClick={elimina}
              disabled={inCorso}
            >
              Elimina bozza
            </button>
          )}
        </div>
      )}

      <form onSubmit={salvaTestata} className="card space-y-4">
        <h2 className="font-semibold text-gray-900">Testata</h2>
        <fieldset disabled={!editabile} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo label="Nome">
              <Testo
                valore={testata.nome}
                onChange={(v) => setTestata((t) => ({ ...t, nome: v }))}
              />
            </Campo>
            {cap.master ? (
              <>
                <Campo
                  label="Peso del Pacchetto"
                  aiuto="Quanto pesano le voci proprie del master nel totale, accanto ai pesi dei sub. 0 = il Pacchetto non conta nel punteggio."
                >
                  <Numero
                    valore={testata.pesoPacchetto}
                    onChange={(v) =>
                      setTestata((t) => ({ ...t, pesoPacchetto: v }))
                    }
                    disabled={!inBozza}
                  />
                </Campo>
                <Campo
                  label="Tipologie di fornitura (dai sub)"
                  className="md:col-span-2"
                  aiuto="Il master eredita le tipologie dei suoi sub; la principale è quella del primo."
                >
                  <p className="text-sm text-gray-700">
                    {(cap.tipologieCoperte || [])
                      .map((t) => t.nome)
                      .join(", ") || "— nessun sub ancora —"}
                  </p>
                </Campo>
              </>
            ) : (
              <Campo
                label="Tipologie di fornitura (servizi)"
                className="md:col-span-2"
                aiuto={
                  inBozza
                    ? "Uno o più servizi: il capitolato vale per tutti. La principale è quella con cui la gara viene classificata."
                    : "Non più modificabili: per cambiarle crea una nuova versione."
                }
              >
                <SceltaMultipla
                  valori={tipologieIds}
                  onChange={setTipologieIds}
                  opzioni={tipologie.map((t) => ({
                    value: t.id,
                    label: t.nome,
                  }))}
                  disabled={!inBozza || !editabile}
                />
              </Campo>
            )}
            <Campo label="Descrizione" className="md:col-span-2">
              <AreaTesto
                valore={testata.descrizione}
                onChange={(v) => setTestata((t) => ({ ...t, descrizione: v }))}
              />
            </Campo>
            <Campo label="Note" className="md:col-span-2">
              <AreaTesto
                valore={testata.note}
                onChange={(v) => setTestata((t) => ({ ...t, note: v }))}
              />
            </Campo>
          </div>
          <button type="submit" className="btn-primary" disabled={inCorso}>
            Salva testata
          </button>
        </fieldset>

        <div className="text-xs text-gray-500 border-t pt-3 space-y-0.5">
          <p>
            Creato da <strong>{cap.creatoDa || "—"}</strong> · modificato da{" "}
            <strong>{cap.modificatoDa || "—"}</strong>
            {cap.dataAttivazione &&
              ` · attivato il ${dataIt(cap.dataAttivazione)}`}
          </p>
          {cap.capitolatoPrecedente && (
            <p>
              Versione precedente:{" "}
              <Link
                className="text-acquisti-700 hover:underline"
                to={`/capitolati/${cap.capitolatoPrecedente.id}`}
              >
                {cap.capitolatoPrecedente.nome} v
                {cap.capitolatoPrecedente.versione}
              </Link>
            </p>
          )}
        </div>
      </form>

      {cap.master && (
        <ComponentiMaster
          cap={cap}
          modificabile={editabile && inBozza}
          azione={azione}
          ricarica={carica}
        />
      )}

      <div className="card space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-semibold text-gray-900">
            {cap.master ? "Voci del Pacchetto" : "Voci"}
          </h2>
          <span className="text-sm text-gray-500">
            {cap.riepilogo.numeroVoci} voci · {cap.riepilogo.numeroObbligatorie}{" "}
            obbligatorie · peso totale{" "}
            <strong>{cap.riepilogo.pesoTotale}</strong>
          </span>
          {editabile && voceInModifica === null && (
            <button
              type="button"
              className="btn-primary ml-auto"
              onClick={() => setVoceInModifica("nuova")}
            >
              + Aggiungi voce
            </button>
          )}
        </div>

        {voceInModifica === "nuova" && (
          <FormVoceGuidato
            iniziale={VOCE_VUOTA}
            pesoAltreVoci={cap.riepilogo.pesoTotale}
            master={cap.master}
            criteri={criteri}
            servizi={servizi}
            inCorso={inCorso}
            onSalva={salvaVoce}
            onAnnulla={() => setVoceInModifica(null)}
          />
        )}

        {(cap.voci || []).length === 0 && voceInModifica !== "nuova" && (
          <p className="text-sm text-gray-500 py-6 text-center">
            {cap.master ? "Nessuna voce del Pacchetto: il master chiede le voci dei suoi sub, queste sono facoltative." : "Nessuna voce. Un capitolato senza voci non si può attivare."}
          </p>
        )}

        {Object.entries(perSezione).map(([sezione, voci]) => (
          <section key={sezione} className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700 flex items-baseline gap-2">
              {sezione === "nessuna"
                ? "Senza sezione"
                : etichetta(SEZIONE_VOCE, Number(sezione))}
              <span className="text-xs font-normal text-gray-500">
                peso {cap.riepilogo.pesoPerSezione?.[sezione] ?? 0}
              </span>
            </h3>

            <div className="divide-y divide-gray-100 border border-gray-200 rounded-md">
              {voci.map((v, i) =>
                voceInModifica === v.id ? (
                  <div key={v.id} className="p-3">
                    <FormVoceGuidato
                      iniziale={{ ...v, servizioId: v.servizioId ?? "" }}
                      pesoAltreVoci={
                        cap.riepilogo.pesoTotale - (Number(v.peso) || 0)
                      }
                      master={cap.master}
                      criteri={criteri}
                      servizi={servizi}
                      inCorso={inCorso}
                      onSalva={salvaVoce}
                      onAnnulla={() => setVoceInModifica(null)}
                    />
                  </div>
                ) : (
                  <div
                    key={v.id}
                    className="p-3 flex flex-wrap gap-x-4 gap-y-1 items-start"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900">
                        {v.nome}
                        {v.obbligatorio && (
                          <span className="badge-danger ml-2">
                            obbligatorio
                          </span>
                        )}
                      </div>
                      {servizi.length > 1 && (
                        <p className="text-xs text-acquisti-700">
                          {v.servizioId
                            ? cap.tipologieCoperte.find(
                                (t) =>
                                  t.id.toLowerCase() ===
                                  v.servizioId.toLowerCase(),
                              )?.nome || "servizio"
                            : "comune a tutti i servizi"}
                        </p>
                      )}
                      {v.descrizione && (
                        <p className="text-sm text-gray-600">{v.descrizione}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        peso {v.peso ?? "—"} · max{" "}
                        {v.punteggioMassimo ?? PUNTEGGIO_MASSIMO_DEFAULT} ·{" "}
                        {etichetta(TIPO_RISPOSTA, v.tipoRisposta)}
                        {v.unitaMisura ? ` (${v.unitaMisura})` : ""} ·{" "}
                        {etichetta(FORMULA, v.formula)}
                        {v.criterioValutazioneId &&
                          " · legata a un criterio di performance"}
                      </p>
                    </div>

                    {editabile && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          className="px-2 py-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                          onClick={() => sposta(v, -1)}
                          disabled={i === 0 || inCorso}
                          title="Sposta su"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                          onClick={() => sposta(v, 1)}
                          disabled={i === voci.length - 1 || inCorso}
                          title="Sposta giù"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 text-acquisti-700 hover:underline text-sm"
                          onClick={() => setVoceInModifica(v.id)}
                        >
                          Modifica
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 text-red-600 hover:underline text-sm"
                          onClick={() => eliminaVoce(v.id, v.nome)}
                        >
                          Elimina
                        </button>
                      </div>
                    )}
                  </div>
                ),
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
